import { Fragment } from "react";
import { Route, Switch } from "react-router-dom";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useChatSupport } from "@hopper-b2b/self-serve";
import { ButtonWrap, Footer as GenericFooter } from "@hopper-b2b/ui";
import { uberOpenExternalLink, useDeviceTypes } from "@hopper-b2b/utilities";
import { Typography } from "@material-ui/core";
import clsx from "clsx";
import type { Pathname } from "history";
import config from "../../utils/config";
import {
  HIDDEN_FOOTER_SEARCH_MOBILE,
  PATH_FLIGHTS_BOOK_CONFIRMATION,
  POSITION_FIXED_FOOTER_PATHS,
} from "../../utils/urlPaths";
import * as constants from "./constants";

import "./styles.scss";

interface IFooterProps {
  pathname?: Pathname;
}

const Footer = ({ pathname }: IFooterProps) => {
  const { matchesMobile } = useDeviceTypes();

  const renderGenericFooter = () => (
    <B2BGenericFooter
      className={clsx("generic-footer", {
        "position-fixed":
          pathname && POSITION_FIXED_FOOTER_PATHS.includes(pathname),
      })}
      isMobile={matchesMobile}
    />
  );

  return (
    <Switch>
      <Route
        path={PATH_FLIGHTS_BOOK_CONFIRMATION}
        render={renderGenericFooter}
        exact
      />
      {HIDDEN_FOOTER_SEARCH_MOBILE.map((path) => {
        return (
          <Route
            key={path}
            path={path}
            render={() => {
              return !matchesMobile ? renderGenericFooter() : null;
            }}
          />
        );
      })}
      <Route path="*" render={renderGenericFooter} />
    </Switch>
  );
};

export default Footer;

export const B2BGenericFooter = ({
  className,
  isMobile,
}: {
  className?: string;
  isMobile?: boolean;
}) => {
  const { generalSupportChat } = useChatSupport();
  const { t } = useI18nContext();
  const renderLinks = () => {
    const renderLink = (texts: string[], urlLink: string) => (
      <ButtonWrap
        onClick={() => {
          uberOpenExternalLink(urlLink);
        }}
      >
        <div className="button-text-wrapper">
          {texts.map((text, index) => (
            <Typography key={index} className="button-text" variant="subtitle2">
              {text}
            </Typography>
          ))}
        </div>
      </ButtonWrap>
    );

    const links = [
      {
        texts: [t("termsAndConditions")],
        urlLink: constants.TERMS_AND_CONDITIONS_LINK,
      },
      { texts: [t("privacyPolicy")], urlLink: constants.PRIVACY_LINK },
    ];

    return (
      <div className="links-wrapper">
        {links.map((link, index) => {
          return (
            <Fragment key={index}>
              {renderLink(link.texts, link.urlLink)}
            </Fragment>
          );
        })}
        <ButtonWrap onClick={() => generalSupportChat()}>
          <div className="button-text-wrapper">
            <Typography className="button-text" variant="subtitle2">
              {t("customerSupport")}
            </Typography>
          </div>
        </ButtonWrap>
      </div>
    );
  };

  return (
    <GenericFooter
      className={className}
      isMobile={isMobile}
      left={<p>{constants.LEGAL}</p>}
      right={<p className="app-version">{config.REACT_APP_VERSION}</p>}
      {...(isMobile ? { top: renderLinks() } : { center: renderLinks() })}
    />
  );
};
